// Password reset. Two modes by URL: with ?uid=&token= → set a new password;
// otherwise → request a reset link by email. Wired to /auth/password/reset/*.

import { useState, type FormEvent } from 'react';

import { EntityDisclaimer } from '../../components/EntityDisclaimer';

import { Honeypot, TurnstileWidget } from '../../lib/turnstile';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Brand, Kicker } from '../../components/shell';
import { api } from '../../lib/api';

export default function PasswordReset() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const uid = params.get('uid');
  const token = params.get('token');
  const confirmMode = !!(uid && token);

  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [captcha, setCaptcha] = useState('');
  const [website, setWebsite] = useState('');
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); setMsg(null); setSubmitting(true);
    try {
      if (confirmMode) {
        await api('/auth/password/reset/confirm', { method: 'POST', body: { uid, token, new_password: newPassword } });
        setDone(true);
        setMsg('Password reset. You can now sign in.');
      } else {
        await api('/auth/password/reset/request', {
          method: 'POST',
          body: { email, website, ...(captcha ? { cf_turnstile_response: captcha } : {}) },
        });
        setDone(true);
        setMsg('If that email exists, a reset link has been sent.');
      }
    } catch (err: any) {
      setError(err?.payload?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--paper)' }}>
      <header style={{ padding: '24px var(--pad-x)', borderBottom: 'var(--hairline)' }}>
        <div style={{ maxWidth: 'var(--max)', margin: '0 auto' }}><Brand onClick={() => navigate('/')} /></div>
      </header>
      <main style={{ flex: 1, display: 'grid', placeItems: 'center', padding: '80px var(--pad-x)' }}>
        <div style={{ width: '100%', maxWidth: 420 }}>
          <Kicker>{confirmMode ? 'SET A NEW PASSWORD' : 'RESET YOUR PASSWORD'}</Kicker>
          <h1 className="page-title" style={{ margin: '8px 0 28px' }}>
            <em>{confirmMode ? 'New password' : 'Reset'}.</em>
          </h1>

          {done ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <p className="serif-body" style={{ margin: 0, fontSize: 16 }}>{msg}</p>
              <button className="btn" onClick={() => navigate('/sign-in')}>To sign in <span className="btn-arrow">→</span></button>
            </div>
          ) : (
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
              {confirmMode ? (
                <div className="field">
                  <label className="field-label">New password</label>
                  <input className="field-input" type="password" value={newPassword}
                         onChange={e => setNewPassword(e.target.value)} autoComplete="new-password" required autoFocus />
                </div>
              ) : (
                <div className="field">
                  <label className="field-label">Email</label>
                  <input className="field-input" type="email" value={email}
                         onChange={e => setEmail(e.target.value)} autoComplete="email" required autoFocus />
                </div>
              )}
              {!confirmMode && (
                <>
                  <Honeypot value={website} onChange={setWebsite} />
                  <TurnstileWidget onToken={setCaptcha} />
                </>
              )}
              {error && <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 15, color: 'var(--accent)' }}>{error}</div>}
              <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                <button type="submit" className="btn" disabled={submitting}>
                  {submitting ? 'Working…' : (confirmMode ? 'Set password' : 'Send reset link')}
                </button>
                <button type="button" className="btn-link" onClick={() => navigate('/sign-in')}>Back to sign in</button>
              </div>
            </form>
          )}
        </div>
      </main>
      <div style={{ padding: '0 var(--pad-x) 20px' }}>
        <EntityDisclaimer style={{ margin: 0 }} />
      </div>
    </div>
  );
}
